'use client';

import { useEffect, useRef, useState } from 'react';
import { Minimize2Icon, RefreshCwIcon, RocketIcon, TrendingUpIcon } from 'lucide-react';
import { Separator } from 'node_modules/@repo/ui/src/separator';

import { Card, CardContent, CardDescription, CardHeader } from '@repo/ui/card';

const challenges = [
  {
    icon: RocketIcon,
    title: 'Launch MVPs Fast',
    description: 'Launch your product faster with an MVP',
  },
  {
    icon: RefreshCwIcon,
    title: 'Modernize Systems',
    description: 'Modernize legacy systems without disruption',
  },
  {
    icon: TrendingUpIcon,
    title: 'Scale Platforms',
    description: 'Scale digital platforms to handle rapid growth.',
  },
  {
    icon: Minimize2Icon,
    title: 'Streamline Processes',
    description: 'Streamline complex processes into simple software',
  },
];

export function ChallengesSection() {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (backgroundRef.current) {
      const parallaxSpeed = 0.5; // Adjust this value to change parallax intensity (0.5 = half speed)
      backgroundRef.current.style.transform = `translateY(${scrollY * parallaxSpeed}px)`;
    }
  }, [scrollY]);

  return (
    <section id="about" className="relative overflow-hidden py-16 dark:bg-muted">
      {/* Parallax background layer */}
      <div
        ref={backgroundRef}
        className="bg-triangles-fade absolute inset-0 -z-10 bg-contain bg-center bg-repeat"
        style={{
          zIndex: 0,
          top: '-50%',
          left: 0,
          right: 0,
          height: '120%',
        }}
      />
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto px-6 text-center">
          <h2 className="mb-12 text-3xl font-bold sm:text-4xl">
            The Challenges We Help Businesses Overcome
          </h2>
        </div>

        <div className="mx-auto my-8 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {challenges.map((feature, index) => {
            const descriptionWords = feature.description.split(' ');
            const keyWord = descriptionWords.shift();
            const description = descriptionWords.join(' ');

            return (
              <Card
                key={index}
                // className="group flex-row rounded-md border border-gray-100 bg-accent bg-gray-400 bg-opacity-10 bg-clip-padding text-center shadow-sm backdrop-blur-sm backdrop-filter transition-all duration-200 hover:-translate-y-1.5 hover:scale-105 hover:shadow-lg"
                className="glass-hover group z-10 flex-row text-center"
              >
                <CardHeader className="pb-4">
                  <div className="mx-auto inline-flex h-16 w-16 items-center justify-center text-primary/90">
                    <feature.icon className="h-12 w-12 group-hover:scale-110 md:h-8 md:w-8" />
                  </div>
                </CardHeader>
                <Separator
                  orientation="horizontal"
                  className="mx-auto w-4/5 bg-muted-foreground/50 dark:group-hover:bg-primary-foreground/50"
                />
                <CardContent className="my-auto mt-4">
                  <CardDescription className="text-base leading-relaxed text-foreground">
                    {keyWord && (
                      <span className="group-hover:font-semibold group-hover:text-primary">
                        {keyWord}{' '}
                      </span>
                    )}
                    {description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
